// ===========================================
// Staff Assign Dialog
// ===========================================
// Assign or edit a staff role for a user

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Autocomplete,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CustomRole, StaffRoleRecord, AssignableUser, customRoleApi } from '../../../api/customRole.api';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';

// ===========================================
// TYPES
// ===========================================

interface StaffAssignDialogProps {
  open: boolean;
  /** null = create / assign mode, otherwise update mode */
  staffRecord: StaffRoleRecord | null;
  roles: CustomRole[];
  onClose: () => void;
  onSave: (data: {
    userId: string;
    customRoleId: string;
    expiresAt?: string | null;
  }) => void;
  isSaving: boolean;
}

// Quick expiry presets
const EXPIRY_PRESETS = [
  { label: '1 Day', getDelta: () => addDays(new Date(), 1) },
  { label: '1 Week', getDelta: () => addDays(new Date(), 7) },
  { label: '1 Month', getDelta: () => addDays(new Date(), 30) },
  { label: 'Permanent', getDelta: () => null },
] as const;

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function toDateTimeLocalString(date: Date | null): string {
  if (!date) return '';
  // Format as "YYYY-MM-DDThh:mm" for datetime-local input
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

// No ADMIN: the picker never offers one (roles slice 3, R3), and the chip shows in the picker only.
const ROLE_LABELS: Record<string, string> = {
  TEACHER: 'Teacher',
  PARENT: 'Parent',
  STUDENT: 'Student',
};

// ===========================================
// COMPONENT
// ===========================================

export default function StaffAssignDialog({
  open,
  staffRecord,
  roles,
  onClose,
  onSave,
  isSaving,
}: StaffAssignDialogProps) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm')); // full screen on a phone (#400)
  const ownerTitle = useOwnerTitle();
  const [selectedUser, setSelectedUser] = useState<AssignableUser | null>(null);
  const [customRoleId, setCustomRoleId] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>(''); // ISO string or empty

  // User search state
  const [userSearchInput, setUserSearchInput] = useState('');
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isEditMode = staffRecord !== null;

  // Populate form in edit mode
  useEffect(() => {
    if (open) {
      if (staffRecord) {
        // In edit mode, create a synthetic user from the staff record
        setSelectedUser({
          id: staffRecord.userId,
          firstName: staffRecord.User.firstName,
          lastName: staffRecord.User.lastName,
          email: staffRecord.User.email,
          role: staffRecord.User.role,
          hasStaffRole: true,
        });
        setCustomRoleId(staffRecord.customRoleId);
        setExpiresAt(
          staffRecord.expiresAt
            ? toDateTimeLocalString(new Date(staffRecord.expiresAt))
            : ''
        );
      } else {
        setSelectedUser(null);
        setCustomRoleId('');
        setExpiresAt('');
        setUserSearchInput('');
        setUsers([]);
      }
    }
  }, [open, staffRecord]);

  // Fetch users when dialog opens or search input changes (debounced for typing)
  useEffect(() => {
    if (isEditMode || !open) return;

    let cancelled = false;
    const delay = userSearchInput ? 300 : 0; // Immediate on open, debounced on typing

    const timeoutId = setTimeout(async () => {
      setLoadingUsers(true);
      try {
        const result = await customRoleApi.getAssignableUsers(
          userSearchInput.trim() || undefined
        );
        if (!cancelled) setUsers(result);
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [userSearchInput, isEditMode, open]);

  // ===========================================
  // HANDLERS
  // ===========================================

  const handlePreset = (getDelta: () => Date | null) => {
    const date = getDelta();
    setExpiresAt(date ? toDateTimeLocalString(date) : '');
  };

  const handleSave = () => {
    if (!selectedUser) return;
    onSave({
      userId: selectedUser.id,
      customRoleId,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
  };

  const canSave =
    selectedUser !== null &&
    customRoleId.length > 0 &&
    !isSaving;

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
    >
      <DialogTitle>
        {isEditMode ? 'Edit Staff Role' : 'Assign Staff Role'}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* User search autocomplete */}
          <Box>
            {isEditMode ? (
              <TextField
                label="User"
                value={`${staffRecord?.User.firstName} ${staffRecord?.User.lastName} (${staffRecord?.User.email})`}
                fullWidth
                disabled
              />
            ) : (
              <Autocomplete
                options={users}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                inputValue={userSearchInput}
                onInputChange={(_, newInputValue) => setUserSearchInput(newInputValue)}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName} (${option.email})`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingUsers}
                filterOptions={(x) => x} // Server-side filtering
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.email}
                        </Typography>
                      </Box>
                      <Chip
                        label={ROLE_LABELS[option.role] || option.role}
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      />
                      {option.hasStaffRole && (
                        <Chip
                          label="Has Role"
                          size="small"
                          color="info"
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search User"
                    placeholder="Type a name or email..."
                    required
                    // Roles slice 3 (R3): the server offers teacher and parent logins only.
                    helperText={`Teacher and parent logins. To move someone with ${ownerTitle} access onto a staff role, use Office access on their Teachers page.`}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={userSearchInput ? 'No users found' : 'Start typing to search...'}
                disabled={isSaving}
              />
            )}
          </Box>

          {/* Role selector */}
          <FormControl fullWidth required>
            <InputLabel id="role-select-label">Custom Role</InputLabel>
            <Select
              labelId="role-select-label"
              value={customRoleId}
              onChange={(e) => setCustomRoleId(e.target.value)}
              label="Custom Role"
              disabled={isSaving}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {role.color && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: role.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {role.name}
                    {role.isSystem && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        (System)
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* No "Grant Super Admin" switch: Full access is retired (roles plan R2); make the person an Admin instead. */}

          {/* Expiry */}
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Access Expiry
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Leave empty for permanent access.
            </Typography>

            {/* Quick-set buttons */}
            {/* A Stack, not a ButtonGroup, so the four presets wrap on a phone (#400). */}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
              {EXPIRY_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  onClick={() => handlePreset(preset.getDelta)}
                  disabled={isSaving}
                  variant={
                    preset.label === 'Permanent' && !expiresAt
                      ? 'contained'
                      : 'outlined'
                  }
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>

            {/* Date/time input */}
            <TextField
              label="Expires At"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              fullWidth
              size="small"
              disabled={isSaving}
              InputLabelProps={{ shrink: true }}
              helperText={expiresAt ? 'Access will expire at this date and time' : 'No expiry — access is permanent'}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isEditMode ? 'Save Changes' : 'Assign Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
